import React from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/ui/Layout';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusHeader from './components/SystemStatusHeader';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import AlertFeed from './components/AlertFeed';
import ServiceAvailabilityMap from './components/ServiceAvailabilityMap';
import BottleneckTable from './components/BottleneckTable';

const OperationsMonitor = () => {
  // Mock operational metrics data
  const operationalMetrics = [
    {
      title: 'System Uptime',
      value: '99.97',
      unit: '%',
      change: 0.02,
      changeType: 'positive',
      icon: 'Server',
      status: 'good',
      threshold: '99.9%',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 99.9 + Math.random() * 0.1
      }))
    },
    {
      title: 'Active Sessions',
      value: '12,847',
      unit: '',
      change: 8.5,
      changeType: 'positive',
      icon: 'Users',
      status: 'normal',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 12000 + Math.random() * 2000
      }))
    },
    {
      title: 'Order Processing',
      value: '1.2',
      unit: 's',
      change: -5.2,
      changeType: 'positive',
      icon: 'Clock',
      status: 'good',
      threshold: '2.0s',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 1.0 + Math.random() * 0.5
      }))
    },
    {
      title: 'Payment Success',
      value: '98.4',
      unit: '%',
      change: -1.2,
      changeType: 'negative',
      icon: 'CreditCard',
      status: 'warning',
      threshold: '99.0%',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 97 + Math.random() * 3
      }))
    },
    {
      title: 'Support Tickets',
      value: '47',
      unit: '',
      change: 12.3,
      changeType: 'negative',
      icon: 'MessageSquare',
      status: 'normal',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 40 + Math.random() * 20
      }))
    },
    {
      title: 'Partner Response',
      value: '2.8',
      unit: 'min',
      change: 3.1,
      changeType: 'negative',
      icon: 'UserCheck',
      status: 'warning',
      threshold: '3.0min',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 2.5 + Math.random() * 1
      }))
    },
    {
      title: 'Delivery Performance',
      value: '94.2',
      unit: '%',
      change: 2.1,
      changeType: 'positive',
      icon: 'Truck',
      status: 'good',
      threshold: '90.0%',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: 90 + Math.random() * 8
      }))
    },
    {
      title: 'Fraud Alerts',
      value: '3',
      unit: '',
      change: -25.0,
      changeType: 'positive',
      icon: 'Shield',
      status: 'critical',
      trendData: Array.from({ length: 12 }, (_, i) => ({
        time: i,
        value: Math.floor(Math.random() * 10)
      }))
    }
  ];

  const handleRefresh = async () => {
    // Simulate refresh delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Data refreshed');
  };

  const handleExport = async () => {
    // Simulate export delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('Report exported');
  };

  const handleFilter = () => {
    console.log('Filter opened');
  };

  return (
    <>
      <Helmet>
        <title>Operations Monitor - Qkly Admin Panel</title>
        <meta name="description" content="Real-time system health and performance tracking across all service verticals for marketplace administrators" />
      </Helmet>
      <Layout>
        <div className="px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <Breadcrumb className="mb-4" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-3xl font-semibold text-foreground">Operations Monitor</h1>
                <p className="text-muted-foreground mt-2">
                  Real-time system health and performance tracking across all service verticals
                </p>
              </div>
              <QuickActionToolbar
                onRefresh={handleRefresh}
                onExport={handleExport}
                onFilter={handleFilter}
                actions={[
                  {
                    id: 'alerts',
                    label: 'View Alerts',
                    icon: 'Bell',
                    variant: 'outline',
                    onClick: () => console.log('Alerts opened')
                  }
                ]}
              />
            </div>
          </div>

          {/* System Status Header */}
          <SystemStatusHeader />

          {/* Operational Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {operationalMetrics?.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                unit={metric?.unit}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                status={metric?.status}
                threshold={metric?.threshold}
                trendData={metric?.trendData}
              />
            ))}
          </div>

          {/* Main Monitoring Area */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
            {/* Performance Chart - Takes 2 columns on xl screens */}
            <div className="xl:col-span-2">
              <PerformanceChart />
            </div>

            {/* Alert Feed - Takes 1 column on xl screens */}
            <div className="xl:col-span-1">
              <AlertFeed />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Service Availability Map */}
            <div>
              <ServiceAvailabilityMap />
            </div>

            {/* Performance Bottlenecks Table */}
            <div>
              <BottleneckTable />
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default OperationsMonitor;